/**
 * Coups de pouce — Attendu 1115 « Reconnaître les solides : cube, pavé,
 * cylindre, prisme droit » (automatisme 4e).
 *
 * quel_*, retrouver_*, nommer_* : entrée PARTAGÉE « reconnaître » — on
 * regarde d'abord s'il y a une surface courbe (cylindre, cône, boule), puis
 * les deux bases superposables (prisme droit), puis les faces toutes
 * rectangulaires (pavé droit) ou toutes carrées (cube). L'exemple : une
 * planche de trois solides (pavé, prisme à base triangulaire, cylindre),
 * dessinée avec les moteurs du gen.
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */
import { paveNomme, cylindreDebout, prismeTri } from '../maths/5eme/espace/svgSolides';
import { planche } from '../maths/4eme/espace/svgSolides4e';

const PLANCHE = planche([
    { label: 'A', type: 'pave', svg: paveNomme({ L: 4.5, H: 2.2, P: 2.8 }, { labels: false, salt: 11 }).svg },
    { label: 'B', type: 'prisme_tri', svg: prismeTri({ salt: 12 }).svg },
    { label: 'C', type: 'cylindre', svg: cylindreDebout({ r: 1.5, h: 3, salt: 13 }).svg },
]).replace(/^<svg [^>]*>/, '').replace(/<\/svg>\s*$/, '');

const T = (x, y, s, couleur, poids = 700, taille = 11) => `<text x="${x}" y="${y}" font-size="${taille}" font-weight="${poids}" fill="${couleur}">${s}</text>`;
const CADRE = (i, couleur) => `<rect x="${i * 150 + 10}" y="${100}" width="130" height="144" rx="8" fill="none" stroke="${couleur}" stroke-width="2.5" stroke-dasharray="7 4"/>`;

const SVG_RECONNAITRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 330" width="100%" style="max-width:420px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : comment s'appellent les solides A, B et C ? En 3 étapes à faire défiler avec les flèches. Étape 1 : une surface courbe ? C a deux disques reliés par une surface courbe : c'est un cylindre. Étape 2 : deux bases superposables reliées par des rectangles ? B a deux triangles devant et derrière : c'est un prisme droit à base triangulaire. Étape 3 : toutes les faces sont des rectangles ? A : oui, c'est un pavé droit ; si toutes ses arêtes étaient égales, ce serait un cube. Réponse : A pavé droit, B prisme droit, C cylindre">
  <style>@keyframes cp1115-pop { from { opacity: 0; } to { opacity: 1; } } .cp1115-pop { opacity: 0; animation: cp1115-pop 0.5s ease forwards; }</style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Une surface courbe ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Deux bases superposables reliées par des rectangles ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Toutes les faces sont des rectangles ?</text>
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${T(8, 44, 'Question : comment s\'appellent les solides A, B et C ?', '#1E3A8A')}
    <g transform="translate(0,78)">${PLANCHE}</g>
    ${CADRE(2, '#1E3A8A')}
    ${T(8, 262, 'C a deux disques reliés par une surface courbe : un cylindre.', '#1E3A8A')}
  </g>
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1115-pop">
    ${CADRE(1, '#1D9E75')}
    ${T(8, 280, 'B a deux triangles superposables, devant et derrière : un prisme droit.', '#1D9E75')}
  </g></g>
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1115-pop">
    ${CADRE(0, '#B45309')}
    ${T(8, 298, 'A a six faces rectangulaires : un pavé droit (un cube si les arêtes étaient égales).', '#B45309')}
    ${T(8, 320, 'Réponse : A est un pavé droit, B un prisme droit, C un cylindre.', '#1e293b', 700, 11.5)}
  </g></g>
</svg>`.trim();

const AIDE_RECONNAITRE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Une surface courbe ?** Deux disques reliés par une surface courbe : un cylindre. Un seul disque et un sommet : un cône. Aucune face plane : une boule.",
                "**Deux bases superposables reliées par des rectangles ?** C'est un prisme droit. Sa base peut être un triangle, un pentagone, un hexagone…",
                "**Toutes les faces sont des rectangles ?** C'est un pavé droit. Si toutes les arêtes ont la même longueur, c'est un cube. Un pavé droit est aussi un prisme droit, et un cube est aussi un pavé droit : on donne le nom le plus précis.",
                "**En perspective cavalière**, les arêtes cachées sont en pointillés et les disques deviennent des ovales : ce n'est pas la forme réelle, regarde les faces et les bases.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_RECONNAITRE },
    ],
};

const CP_1115 = {
    quel_qcm_unique: AIDE_RECONNAITRE,
    retrouver_qcm_unique: AIDE_RECONNAITRE,
    nommer_relier: AIDE_RECONNAITRE,
};

export default CP_1115;
